import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Calendar, Edit, Trash2, Eye, Workflow } from 'lucide-react';
import { getArticles, deleteArticle } from '../services/api';

function ArticlesList() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const data = await getArticles();
      setArticles(data);
    } catch (err) {
      setError('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      try {
        await deleteArticle(id);
        setArticles(articles.filter(article => article.id !== id));
      } catch (err) {
        setError('Failed to delete article');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <p className="text-red-800">{error}</p>
        <button 
          onClick={loadArticles}
          className="mt-4 text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Articles Library</h1>
        <p className="text-gray-600">
          View and manage your processed PDF articles and generated content
        </p>
      </div>

      {articles.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <FileText className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No articles yet</h3>
          <p className="text-gray-600 mb-6">
            Upload your first PDF article to get started with AI-powered content generation
          </p>
          <Link
            to="/upload"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
          >
            Upload PDF
          </Link>
        </div>
      ) : (
        <div className="grid gap-6">
          {articles.map((article) => (
            <div key={article.id} className="bg-white rounded-lg shadow-sm border p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-3">
                    <FileText className="h-6 w-6 text-primary-500" />
                    <h3 className="text-xl font-semibold text-gray-900">
                      {article.title}
                    </h3>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <Calendar className="h-4 w-4 mr-2" />
                    Created {formatDate(article.created_at)}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
                    <div className={`p-3 rounded-lg ${article.summary ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className="text-sm font-medium text-gray-900">Summary</div>
                      <div className={`text-xs ${article.summary ? 'text-green-700' : 'text-gray-500'}`}>
                        {article.summary ? 'Generated' : 'Not generated'}
                      </div>
                    </div>
                    
                    <div className={`p-3 rounded-lg ${article.short_script ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className="text-sm font-medium text-gray-900">Short Script</div>
                      <div className={`text-xs ${article.short_script ? 'text-green-700' : 'text-gray-500'}`}>
                        {article.short_script ? 'Generated' : 'Not generated'}
                      </div>
                    </div>
                    
                    <div className={`p-3 rounded-lg ${article.long_script ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className="text-sm font-medium text-gray-900">Long Script</div>
                      <div className={`text-xs ${article.long_script ? 'text-green-700' : 'text-gray-500'}`}>
                        {article.long_script ? 'Generated' : 'Not generated'}
                      </div>
                    </div>
                    
                    <div className={`p-3 rounded-lg ${article.short_script_text ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className="text-sm font-medium text-gray-900">Short Text</div>
                      <div className={`text-xs ${article.short_script_text ? 'text-blue-700' : 'text-gray-500'}`}>
                        {article.short_script_text ? 'Generated' : 'Not generated'}
                      </div>
                    </div>
                    
                    <div className={`p-3 rounded-lg ${article.long_script_text ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                      <div className="text-sm font-medium text-gray-900">Long Text</div>
                      <div className={`text-xs ${article.long_script_text ? 'text-blue-700' : 'text-gray-500'}`}>
                        {article.long_script_text ? 'Generated' : 'Not generated'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <Link
                    to={`/articles/${article.id}`}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View
                  </Link>
                  
                  <Link
                    to={`/articles/${article.id}`}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Link>
                  
                  <Link
                    to={`/workflow/${article.id}`}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <Workflow className="h-4 w-4 mr-2" />
                    Workflow
                  </Link>
                  
                  <button
                    onClick={() => handleDelete(article.id, article.title)}
                    className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ArticlesList;
